import { faAndroid } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Disclosure, DisclosureButton, Labeled } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiGetResourcesResponseBody } from '~/lib/api/modules/AppResources';
import { FormSection } from '~/components/atoms/FormSection';
import { AssetUploader } from '~/components/organisms/fields/AssetUploader';

export type IAssetsSettingsFormAndroidSectionProps = IContainerProps & {
  appId: string;
  assets: IApiGetResourcesResponseBody;
};

export const AssetsSettingsFormAndroidSection: React.FC<
  IAssetsSettingsFormAndroidSectionProps
> = (props) => {
  const { sx, appId, assets } = props;
  const { t } = useTranslation();

  return (
    <Disclosure
      sx={sx}
      defaultExpanded
      trigger={
        <DisclosureButton leadingIcon={<FontAwesomeIcon icon={faAndroid} />}>
          Android
        </DisclosureButton>
      }
    >
      <FormSection sub>
        <Labeled
          label={t('AssetsSettingsFormAndroidSection.fields.icon.label')}
        >
          {(elementProps) => (
            <AssetUploader
              appId={appId}
              type="android"
              format="icon"
              accept={{ 'image/png': ['.png'] }}
              acceptWidth={{ min: 1024 }}
              acceptHeight={{ min: 1024 }}
              supportingText={t(
                'AssetsSettingsFormAndroidSection.fields.icon.dropZoneSupportingText',
              )}
              downloadUrl={assets.platforms?.android?.appIcon?.downloadUrl}
              {...elementProps}
            />
          )}
        </Labeled>
        <Labeled
          label={t(
            'AssetsSettingsFormAndroidSection.fields.splashScreen.label',
          )}
        >
          {(elementProps) => (
            <AssetUploader
              appId={appId}
              type="android"
              format="splash"
              accept={{ 'image/png': ['.png'] }}
              acceptWidth={{ min: 2732 }}
              acceptHeight={{ min: 2732 }}
              supportingText={t(
                'AssetsSettingsFormAndroidSection.fields.splashScreen.dropZoneSupportingText',
              )}
              downloadUrl={assets.platforms?.android?.splashScreen?.downloadUrl}
              {...elementProps}
            />
          )}
        </Labeled>
        <Labeled
          label={t(
            'AssetsSettingsFormAndroidSection.fields.iconBackgroundLayer.label',
          )}
          supportingText={t(
            'AssetsSettingsFormAndroidSection.fields.iconBackgroundLayer.supportingText',
          )}
        >
          {(elementProps) => (
            <AssetUploader
              appId={appId}
              type="android"
              format="background"
              accept={{ 'image/png': ['.png'] }}
              supportingText={t(
                'AssetsSettingsFormAndroidSection.fields.iconBackgroundLayer.dropZoneSupportingText',
              )}
              acceptWidth={{ min: 432 }}
              acceptHeight={{ min: 432 }}
              downloadUrl={
                assets.platforms?.android?.appIconBackgroundLayer?.downloadUrl
              }
              {...elementProps}
            />
          )}
        </Labeled>
        <Labeled
          label={t(
            'AssetsSettingsFormAndroidSection.fields.iconForegroundLayer.label',
          )}
          supportingText={t(
            'AssetsSettingsFormAndroidSection.fields.iconForegroundLayer.supportingText',
          )}
        >
          {(elementProps) => (
            <AssetUploader
              appId={appId}
              type="android"
              format="foreground"
              accept={{ 'image/png': ['.png'] }}
              supportingText={t(
                'AssetsSettingsFormAndroidSection.fields.iconForegroundLayer.dropZoneSupportingText',
              )}
              acceptWidth={{ min: 432 }}
              acceptHeight={{ min: 432 }}
              downloadUrl={
                assets.platforms?.android?.appIconForegroundLayer?.downloadUrl
              }
              {...elementProps}
            />
          )}
        </Labeled>
      </FormSection>
    </Disclosure>
  );
};
